import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELDS = [["name", "Ragione sociale *"], ["referent", "Nome referente"], ["phone", "Telefono"], ["email", "Email"]];

const payload = (f, force) => {
  const [first, ...rest] = f.referent.trim().split(/\s+/);
  const v = (x) => x.trim() || null;
  return { name: f.name.trim(), status: "da_qualificare", phone: v(f.phone), email: v(f.email), force,
    contact: first ? { first_name: first, last_name: rest.join(" "), phone: v(f.phone), email: v(f.email), is_primary: true } : null };
};

const DupList = ({ dups, onPick }) => (
  <div className="space-y-2 rounded-control border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="quick-company-dups">
    <p className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" />Esistono clienti simili</p>
    {dups.matches.map((d) => (
      <button key={d.id} type="button" onClick={() => onPick(d)} className="flex w-full items-center justify-between rounded-control bg-white px-3 py-2 text-left hover:bg-slate-50" data-testid={`quick-company-dup-${d.id}`}>
        <span className="font-medium text-ink">{d.name}{d.city && <span className="ml-2 text-xs text-slate-500">{d.city}</span>}</span>
        <span className="text-xs text-brand-700">Usa questo</span>
      </button>
    ))}
    {dups.hidden > 0 && <p className="text-xs text-amber-700">Altri {dups.hidden} non visibili con i tuoi permessi.</p>}
  </div>
);

export const QuickCompanyDialog = ({ open, onOpenChange, initialName, onCreated }) => {
  const [f, setF] = useState({ name: "", referent: "", phone: "", email: "" });
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ name: initialName || "", referent: "", phone: "", email: "" }); setDups(null); } }, [open, initialName]);

  const done = (c) => { onCreated(c); onOpenChange(false); };
  const save = async (force = false) => {
    setBusy(true);
    try {
      if (!force) {
        const { data } = await api.post("/crm/companies/check-duplicates", { name: f.name.trim() });
        if (data.matches.length || data.hidden) { setDups(data); return; }
      }
      const { data } = await api.post("/crm/companies", payload(f, force));
      toast.success("Cliente creato (da qualificare)");
      done({ id: data.id, name: f.name.trim() });
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-company-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Nuovo cliente</DialogTitle>
          <DialogDescription>Dati base: il cliente nasce "Da qualificare" e lo completi dopo dall'anagrafica.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{label}</Label>
              <Input value={f[k]} maxLength={k === "name" ? 200 : 120} type={k === "email" ? "email" : "text"}
                onChange={(e) => { setF({ ...f, [k]: e.target.value }); if (k === "name") setDups(null); }} data-testid={`quick-company-${k}`} /></div>
          ))}
          {dups && <DupList dups={dups} onPick={(d) => done({ id: d.id, name: d.name })} />}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          {dups ? <Button onClick={() => save(true)} disabled={busy} data-testid="quick-company-force">Crea comunque</Button>
            : <Button onClick={() => save()} disabled={busy || f.name.trim().length < 2} data-testid="quick-company-save">Crea cliente</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
